/**
 * PDF Export Styles and Configuration
 *
 * This utility provides shared CSS styles and html3pdf configuration
 * for consistent PDF generation across the application.
 *
 * Used by:
 * - DealSummaryEditor (One Pager)
 * - EngagementLetterEditor (Engagement Letter)
 *
 * @author AICA Team
 * @version 1.0.0
 */

/**
 * Base text formatting styles for PDF export.
 * Handles bold, italic, underline, strikethrough and all their combinations.
 */
const baseFormattingStyles = `
    /* Base formatting */
    strong, b {
        font-weight: bold !important;
    }
    
    em, i {
        font-style: italic !important;
    }
    
    /* Base strikethrough */
    s, del, strike {
        text-decoration: line-through !important;
        text-decoration-color: currentColor !important;
    }
    
    /* Underline */
    u {
        text-decoration: underline !important;
        text-decoration-color: currentColor !important;
    }
    
    /* Multiple text decorations (underline + strikethrough) */
    u s, u del, u strike,
    s u, del u, strike u {
        text-decoration: underline line-through !important;
        text-decoration-color: currentColor !important;
    }
`;

/**
 * List-specific text decoration styles.
 * Ensures strikethrough and underline work correctly within list items.
 */
const listFormattingStyles = `
    /* Strikethrough in lists */
    li s, li del, li strike,
    ul s, ul del, ul strike,
    ol s, ol del, ol strike {
        text-decoration: line-through !important;
        -webkit-text-decoration: line-through !important;
        text-decoration-color: currentColor !important;
    }
    
    /* Underline in lists */
    li u, ul u, ol u {
        text-decoration: underline !important;
        -webkit-text-decoration: underline !important;
        text-decoration-color: currentColor !important;
    }
    
    /* Multiple decorations in lists */
    li u s, li u del, li u strike,
    li s u, li del u, li strike u {
        text-decoration: underline line-through !important;
        -webkit-text-decoration: underline line-through !important;
        text-decoration-color: currentColor !important;
    }
`;

/**
 * Bold combination styles.
 * Handles bold + strikethrough, bold + underline, and triple combinations.
 */
const boldCombinationStyles = `
    /* Bold + Strikethrough */
    strong s, strong del, strong strike,
    s strong, del strong, strike strong,
    b s, b del, b strike,
    s b, del b, strike b,
    strong > s, strong > del, strong > strike,
    s > strong, del > strong, strike > strong {
        font-weight: bold !important;
        text-decoration: line-through !important;
        -webkit-text-decoration: line-through !important;
        text-decoration-color: currentColor !important;
    }
    
    /* Bold + Underline */
    strong u, u strong,
    b u, u b,
    strong > u, u > strong {
        font-weight: bold !important;
        text-decoration: underline !important;
        -webkit-text-decoration: underline !important;
        text-decoration-color: currentColor !important;
    }
    
    /* Bold + Underline + Strikethrough (all permutations) */
    strong u s, strong u del, strong u strike,
    strong s u, strong del u, strong strike u,
    u strong s, u strong del, u strong strike,
    s strong u, del strong u, strike strong u,
    u s strong, u del strong, u strike strong,
    s u strong, del u strong, strike u strong,
    b u s, b u del, b u strike,
    b s u, b del u, b strike u,
    u b s, u b del, u b strike,
    s b u, del b u, strike b u,
    u s b, u del b, u strike b,
    s u b, del u b, strike u b,
    strong > u > s, strong > u > del, strong > u > strike,
    strong > s > u, strong > del > u, strong > strike > u,
    u > strong > s, u > strong > del, u > strong > strike,
    s > strong > u, del > strong > u, strike > strong > u {
        font-weight: bold !important;
        text-decoration: underline line-through !important;
        -webkit-text-decoration: underline line-through !important;
        text-decoration-color: currentColor !important;
    }
`;

/**
 * Italic combination styles.
 */
const italicCombinationStyles = `
    /* Italic + Strikethrough */
    em s, em del, em strike,
    i s, i del, i strike,
    s em, del em, strike em,
    s i, del i, strike i {
        font-style: italic !important;
        text-decoration: line-through !important;
    }
    
    /* Italic + Underline */
    em u, i u, u em, u i {
        font-style: italic !important;
        text-decoration: underline !important;
    }
`;

/**
 * Inheritance styles to ensure nested elements maintain proper formatting.
 */
const inheritanceStyles = `
    /* Ensure span doesn't override */
    span {
        text-decoration: inherit !important;
        font-weight: inherit !important;
        font-style: inherit !important;
    }
    
    /* Ensure all children inherit decorations in bold combinations */
    strong s *, strong del *, strong strike *,
    s strong *, del strong *, strike strong *,
    strong u *, u strong *,
    strong u s *, strong s u *,
    u strong s *, s strong u * {
        text-decoration: inherit !important;
        font-weight: inherit !important;
    }
`;

/**
 * List styling for PDF export.
 */
const listStyles = `
    /* Lists */
    ul, ol {
        padding-left: 24px !important;
        margin-bottom: 8px !important;
    }
    ul {
        list-style-type: disc !important;
    }
    ol {
        list-style-type: decimal !important;
    }
    li {
        margin-bottom: 4px !important;
    }
`;

/**
 * Table styling for PDF export.
 */
const tableStyles = `
    /* Tables */
    table {
        border-collapse: collapse;
        width: 100%;
        margin-bottom: 16px;
    }
    th, td {
        border: 1px solid #ddd;
        padding: 8px;
        text-align: left;
    }
    th {
        background-color: #f5f5f5;
        font-weight: bold;
    }
`;

/**
 * Page break handling styles to prevent elements from being cut between pages.
 * NOTE: We intentionally allow tables to break across pages for continuous flow.
 * Only individual table rows are protected from splitting.
 */
const pageBreakStyles = `
    /* Headings - prevent breaking */
    h1, h2, h3, h4, h5, h6 {
        margin-top: 16px;
        margin-bottom: 8px;
        page-break-after: avoid !important;
        break-after: avoid !important;
        page-break-inside: avoid !important;
        break-inside: avoid !important;
    }
    
    /* Paragraphs - control orphans and widows */
    div, p {
        margin-bottom: 4px;
        orphans: 3;
        widows: 3;
    }
    
    /* Tables - allow breaking across pages for continuous flow */
    table {
        /* No page-break-inside: avoid - tables can span pages */
        width: 100%;
        margin-bottom: 16px;
    }
    
    /* Prevent table rows from breaking - keeps each row intact */
    tr {
        page-break-inside: avoid !important;
        break-inside: avoid !important;
    }
    
    /* Prevent list items from being orphaned */
    li {
        page-break-inside: avoid !important;
        break-inside: avoid !important;
    }
    
    /* Prevent images from being cut */
    img {
        page-break-inside: avoid !important;
        break-inside: avoid !important;
        max-width: 100%;
    }
    
    /* Ensure headings stay with following content */
    h1 + *, h2 + *, h3 + *, h4 + *, h5 + *, h6 + * {
        page-break-before: avoid !important;
        break-before: avoid !important;
    }
`;

/**
 * Complete PDF export styles combining all style modules.
 * Use this in the generateHTMLFromSlateContent function.
 */
export const getPdfExportStyles = (): string => {
    return `
        /* Base PDF styles */
        * {
            font-family: Arial, Helvetica, sans-serif;
            line-height: 1.5;
            box-sizing: border-box;
        }
        
        /* Content wrapper - no transform to ensure container height matches content exactly */
        .content {
            padding: 5px;
            margin: 0;
        }
        
        ${baseFormattingStyles}
        ${listFormattingStyles}
        ${boldCombinationStyles}
        ${italicCombinationStyles}
        ${inheritanceStyles}
        ${listStyles}
        ${tableStyles}
        ${pageBreakStyles}
    `;
};

/**
 * HTML3PDF configuration options for PDF generation.
 * Provides consistent settings across all PDF exports.
 */
export interface Html3PdfOptions {
    padding?: number;
    margin?: number | number[];
    filename?: string;
    html2canvas?: {
        scale?: number;
        useCORS?: boolean;
        dpi?: number;
        allowTaint?: boolean;
    };
    jsPDF?: {
        unit?: string;
        format?: string;
        orientation?: string;
    };
    pagebreak?: {
        mode?: string[];
        before?: string;
        after?: string;
        avoid?: string[];
    };
}

/**
 * Default html3pdf options for PDF generation.
 * These settings ensure:
 * - High quality output (scale: 2, dpi: 300)
 * - Proper page breaks (avoid cutting elements)
 * - Consistent margins
 *
 * @param filename - Optional filename for the PDF
 * @returns Html3PdfOptions configuration object
 */
export const getDefaultPdfOptions = (filename?: string): Html3PdfOptions => ({
    margin: [15, 15, 15, 15], // top, left, bottom, right in mm - increased margins for professional look
    filename: filename || 'document.pdf',
    html2canvas: {
        scale: 2,
        useCORS: true,
        dpi: 300,
        allowTaint: false,
    },
    jsPDF: {
        unit: 'mm',
        format: 'a3',
        orientation: 'portrait',
    },
    pagebreak: {
        mode: ['css', 'legacy'],
        before: '.page-break-before',
        after: '.page-break-after',
    },
});

/**
 * Creates a complete HTML document for PDF export.
 * Wraps the content with proper styling.
 *
 * @param htmlContent - The HTML content to wrap
 * @returns Complete HTML string with styles
 */
export const createPdfExportHtml = (htmlContent: string): string => {
    return `
        <style>
            ${getPdfExportStyles()}
        </style>
        <div class="content">${htmlContent}</div>
    `;
};

/**
 * Exports all page break avoid elements as a constant.
 * Useful for custom configurations.
 */
export const PAGE_BREAK_AVOID_ELEMENTS = ['tr', 'th', 'td', 'li', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'table', 'img'] as const;

export default {
    getPdfExportStyles,
    getDefaultPdfOptions,
    createPdfExportHtml,
    PAGE_BREAK_AVOID_ELEMENTS,
};
